import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { StateSelect } from '@/components/ui/StateSelect'
import { useVendors } from '../../hooks/useVendors'
import { VehicleClass, VehicleClassLabels, WeightClass, WeightClassLabels } from '../../models/equipment'
import { type FailureFinding, FixKind, ValueSource } from '../../models/indicationFailure'

const SOURCE_LABELS: Record<ValueSource, string> = {
  [ValueSource.CarrierError]: "from the carrier's own message",
  [ValueSource.VinDecode]: 'from the VIN decode',
  [ValueSource.Fmcsa]: 'from FMCSA',
  [ValueSource.ExistingRecord]: 'from another record on file',
  [ValueSource.None]: '',
}

/** Declaration order, not key order — WeightClassLabels is keyed '3'-'8' and 'A'-'H',
 *  and Object.entries hoists the integer-like keys, listing the heaviest classes first. */
function orderedOptions<T extends string>(members: Record<string, T>, labels: Record<T, string>) {
  return Object.values(members).map((value) => ({ value, label: labels[value] }))
}

// A free-text box on an enum invites a value the carrier already rejected.
export const ENUM_OPTIONS: Record<string, { value: string; label: string }[] | undefined> = {
  'Truck.weight_class': orderedOptions(WeightClass, WeightClassLabels),
  'Trailer.weight_class': orderedOptions(WeightClass, WeightClassLabels),
  'Truck.vehicle_class': orderedOptions(VehicleClass, VehicleClassLabels),
  'Trailer.vehicle_class': orderedOptions(VehicleClass, VehicleClassLabels),
}

export interface FindingDraft {
  selected: boolean
  value: string
}

export function FailureFindingRow({
  finding,
  draft,
  onChange,
  onDismiss,
}: {
  finding: FailureFinding
  draft: FindingDraft
  onChange: (draft: FindingDraft) => void
  onDismiss: () => void
}) {
  // One cached query for the whole dialog; falls back to the raw code if it misses.
  const { vendorLabel } = useVendors()
  const source = SOURCE_LABELS[finding.valueSource]
  const fieldLabel = finding.targetField.replace(/_/g, ' ')

  function renderControl() {
    if (finding.fixKind === FixKind.FieldEdit) {
      const options = ENUM_OPTIONS[`${finding.targetModel}.${finding.targetField}`]
      // Ticked with nothing to send: the fix is held back until a value is chosen.
      const missing = draft.selected && !draft.value.trim()
      return (
        <div className="flex flex-col gap-1">
          {options ? (
            <Select value={draft.value} onValueChange={(value) => onChange({ ...draft, value })}>
              <SelectTrigger aria-label={`New ${fieldLabel}`} aria-invalid={missing}>
                <SelectValue placeholder={`New ${fieldLabel}`} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : finding.targetField === 'license_state' ? (
            <StateSelect
              error={missing}
              value={draft.value}
              onChange={(value) => onChange({ ...draft, value })}
            />
          ) : (
            <Input
              value={draft.value}
              aria-label={`New ${fieldLabel}`}
              aria-invalid={missing}
              placeholder={`New ${fieldLabel}`}
              onChange={(event) => onChange({ ...draft, value: event.target.value })}
            />
          )}
          {finding.currentValue && (
            <span className="text-muted-foreground text-xs">
              Currently{' '}
              {options?.find((o) => o.value === finding.currentValue)?.label ?? finding.currentValue}
            </span>
          )}
          {source && <span className="text-muted-foreground text-xs">Suggested {source}</span>}
        </div>
      )
    }
    if (finding.fixKind === FixKind.RemoveUnit) {
      return <span className="text-muted-foreground text-sm">Removes this unit from the schedule.</span>
    }
    if (finding.fixKind === FixKind.DocumentUpload) {
      return (
        <span className="text-muted-foreground text-sm">
          Upload the {fieldLabel.toLowerCase()} from the quote card.
        </span>
      )
    }
    if (finding.fixKind === FixKind.CoverageChange) {
      return (
        <span className="text-muted-foreground text-sm">Change the coverage selection, then requote.</span>
      )
    }
    return <span className="text-muted-foreground text-sm">No automatic fix — handle this by hand.</span>
  }

  return (
    <div className="flex gap-3 border-b py-4 last:border-b-0">
      <Checkbox
        className="mt-1"
        checked={draft.selected}
        disabled={!finding.isFixable}
        aria-label={`Fix ${finding.title}`}
        onCheckedChange={(checked) => onChange({ ...draft, selected: checked === true })}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-md font-semibold">{finding.title}</span>
          {finding.blockedVendors.map((vendor) => (
            <Badge key={vendor} variant="secondary">
              {vendorLabel(vendor)}
            </Badge>
          ))}
        </div>
        {finding.detail && <p className="text-muted-foreground text-sm">{finding.detail}</p>}
        {renderControl()}
      </div>
      <Button size="sm" variant="ghost" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  )
}
